import { useEffect, useRef } from 'preact/hooks';
import {
  Events as CurrencyEvents,
  getDataAttributes as getCurrencyDataAttributes,
  type DataAttributes as CurrencyDataAttributes,
} from './currency';
import {
  Events as DateTimeEvents,
  getDataAttributes as getDateTimeDataAttributes,
  type DataAttributes as DateTimeDataAttributes,
} from './datetime';
import {
  Events as PercentageEvents,
  getDataAttributes as getPercentageDataAttributes,
  type DataAttributes as PercentageDataAttributes,
} from './percentage';
import {
  Events as PhoneEvents,
  getDataAttributes as getPhoneDataAttributes,
  type DataAttributes as PhoneDataAttributes,
} from './phone';

import { EMPTY_OBJECT, formatDate, isEqual, NOOP } from '../../utility';
import { useMergedRef } from '../../hooks/useMergedRef';
import type { HTMLAttributes } from '../../types/utility';

export type CurrencyOptions = { type: 'currency' } & CurrencyDataAttributes;
export type DateTimeOptions = { type: 'datetime' } & DateTimeDataAttributes;
export type PercentageOptions = { type: 'percentage' } & PercentageDataAttributes;
export type PhoneOptions = { type: 'phone' } & PhoneDataAttributes;

type Props = {
  fieldProps: HTMLAttributes<HTMLInputElement>;
  options: CurrencyOptions | DateTimeOptions | PercentageOptions | PhoneOptions;
};

const INPUT_MASKS = {
  currency: CurrencyEvents,
  datetime: DateTimeEvents,
  percentage: PercentageEvents,
  phone: PhoneEvents,
};

const GET_DATA_ATTRIBUTES = {
  currency: getCurrencyDataAttributes,
  datetime: getDateTimeDataAttributes,
  percentage: getPercentageDataAttributes,
  phone: getPhoneDataAttributes,
};

export default function InputMask({ fieldProps, options }: Props) {
  // pull any forwarded ref out of fieldProps so the spread below can't
  // clobber our internal ref (the mask's event wiring depends on it)
  const { ref: forwardedRef, ...inputProps } = fieldProps;
  const { type, ...attributes } = options;
  const isInitialRenderRef = useRef(true);
  const inputRef = useRef<HTMLInputElement>(null);
  const mergedInputRef = useMergedRef(inputRef, forwardedRef);
  const inputMask = INPUT_MASKS[type] ?? EMPTY_OBJECT;
  const getDataAttributes = GET_DATA_ATTRIBUTES[type] ?? NOOP;

  // add configured event handlers
  useEffect(() => {
    const { current } = inputRef;
    const events = Object.entries(inputMask) as [keyof HTMLElementEventMap, (e: Event) => void][];

    if (current) {
      events.forEach(([event, callback]) => {
        current.addEventListener(event, callback, true);
      });
    }

    return () => {
      if (current) {
        events.forEach(([event, callback]) => {
          current.removeEventListener(event, callback, true);
        });
      }
    };
  }, [inputMask]);

  // force formatting on mount and options change
  const prevOptionsRef = useRef<Props['options']>();
  useEffect(() => {
    if (isEqual(prevOptionsRef.current, options)) {
      return;
    }

    prevOptionsRef.current = options;

    inputRef.current?.dispatchEvent(
      new CustomEvent('input', { detail: { isInitialRender: isInitialRenderRef.current } }),
    );

    if (isInitialRenderRef.current) {
      isInitialRenderRef.current = false;
    }
  }, [options]);

  // @ts-ignore prop `output` can vary between the `type` and enforced at the component/prop-level
  const dataAttributes = getDataAttributes(attributes);

  // name is undefined so the hidden field value is serialized rather than the formatted one
  return (
    <>
      <input
        autoComplete="off"
        ref={mergedInputRef}
        {...inputProps}
        name={undefined}
        {...dataAttributes}
        data-name={fieldProps.name}
      />
      <input
        type="hidden"
        name={fieldProps.name}
        defaultValue={fieldProps.value as string}
        disabled={!!fieldProps.disabled}
        data-input-mask="true"
      />
    </>
  );
}

// helpers for getting <InputMask> values in unit tests
export function getInputMask(key: string | HTMLElement, container?: Element) {
  let element;

  if (typeof key === 'string') {
    element = (container ?? document).querySelector(`[data-name="${key}"]`) as HTMLInputElement;
  } else {
    element = key;
  }

  if (!element) {
    return;
  }

  const { parentElement } = element;

  return {
    input: parentElement.querySelector('input[data-name]') as HTMLInputElement,
    hiddenInput: parentElement.querySelector(`input[type="hidden"][name]`) as HTMLInputElement,
  };
}

export function setInputMask(element: HTMLInputElement, value: string, isInitialRender = false) {
  if (element.type === 'datetime-local') {
    const date = value ? new Date(value) : undefined;
    element.value = formatDate(
      date,
      { type: 'iso-local', format: 'YYYY-MM-DDThh:mm' },
      element.dataset.timeZone,
    );
  } else {
    element.value = value;
  }
  element.dispatchEvent(new CustomEvent('input', { bubbles: true, detail: { isInitialRender } }));
}
